import { themeQuartz } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { Plus, Trash2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import images from "../../../assets/images";
import Modal from "../../../components/modal/Modal";
import {
  useHazardousMaterialStore,
  useTrackingMovementStore,
} from "../../../store/hazardousMaterialStore";
import {
  useLocationStore,
  useUnitsofMeasureStore,
} from "../../../store/masterDataStore";
import { useOrgUsersStore } from "../../../store/store";
import { OrgUserType, TrackingMovementType } from "../../../types/api";

const gridTheme = themeQuartz.withParams({
  backgroundColor: "#fff",
  foregroundColor: "#2a2a2a",
  headerTextColor: "#2a2a2a",
  headerBackgroundColor: "#E9F5FF",
  oddRowBackgroundColor: "rgba(0, 0, 0, 0.03)",
  headerColumnResizeHandleColor: "rgb(126, 46, 132)",
  fontSize: "0.75rem",
  headerFontWeight: "bold",
});

const emptyForm: TrackingMovementType = {
  materialName: "",
  fromLocation: "",
  toLocation: "",
  quantityMoved: "",
  unitOfMeasure: "",
  movementDateTime: "",
  approvedBy: "",
  remarks: "",
};

const transportMethods = ["Manual", "Forklift", "Conveyor Belt", "Other"];
const movementStatusOptions = ["Completed", "In Transit", "Delayed"];
const conditionOptions = ["Intact", "Damaged", "Other"];

const TrackingMovementLogs: React.FC = () => {
  const [rowData, setRowData] = useState<TrackingMovementType[]>([]);
  const [isModalOpen, setModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [formData, setFormData] = useState<TrackingMovementType>(emptyForm);
  const [editId, setEditId] = useState<string | null>(null);

  const {
    trackingMovementRecords,
    loading,
    fetchAllTrackingMovement,
    createTrackingMovement,
    updateTrackingMovement,
    deleteTrackingMovement,
  } = useTrackingMovementStore();

  const {
    orgUsersData,
    orgUsersMap,
    loading: orgUsersLoading,
    fetchOrgUsers,
  } = useOrgUsersStore();

  const {
    allLocations,
    allLocationsMap,
    loading: locationsLoading,
    fetchAllLocations,
  } = useLocationStore();

  const {
    hazardousMaterials,
    materialDataMap,
    loading: hazardousMaterialsLoading,
    fetchAllHazardousMaterials,
  } = useHazardousMaterialStore();

  const {
    unitsofMeasure,
    unitsofMeasureMap,
    loading: unitsofMeasureLoading,
    fetchAllUnitsofMeasure,
  } = useUnitsofMeasureStore();

  useEffect(() => {
    fetchAllTrackingMovement();
    if (!orgUsersData.length) fetchOrgUsers();
    if (!allLocations.length) fetchAllLocations();
    if (!hazardousMaterials.length) fetchAllHazardousMaterials();
    if (!unitsofMeasure.length) fetchAllUnitsofMeasure();
  }, [
    fetchAllTrackingMovement,
    fetchOrgUsers,
    fetchAllLocations,
    fetchAllHazardousMaterials,
    fetchAllUnitsofMeasure,
  ]);

  useEffect(() => {
    setRowData(trackingMovementRecords ?? []);
  }, [trackingMovementRecords]);

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value } = e.target;
    if (name === "materialName") {
      const material = hazardousMaterials.find((m) => m._id === value);
      setFormData((prev) => ({
        ...prev,
        [name]: value,
        unitOfMeasure: material?.unitOfMeasure || "",
      }));
    } else if (name === "quantityMoved") {
      const material = hazardousMaterials.find(
        (m) => m._id === formData.materialName
      );
      if (material?.quantity && Number(material.quantity) < Number(value)) {
        alert("Selected quantity is greater than the available quantity");
        return;
      } else {
        setFormData((prev) => ({
          ...prev,
          [name]: value,
        }));
      }
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    let success = false;
    if (isEditMode && editId) {
      success = await updateTrackingMovement(formData, editId);
    } else {
      success = await createTrackingMovement(formData);
    }
    if (success) {
      setModalOpen(false);
      setFormData(emptyForm);
      setEditId(null);
      setIsEditMode(false);
    }
  };

  const handleEdit = (data: TrackingMovementType) => {
    setIsEditMode(true);
    setFormData({ ...data });
    setEditId(data._id || "");
    setModalOpen(true);
  };

  const handleDelete = async (id: string) => {
    const confirmed = window.confirm(
      "Are you sure you want to delete this tracking and movement record?"
    );
    if (confirmed) {
      await deleteTrackingMovement(id);
    }
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setFormData(emptyForm);
    setEditId(null);
    setIsEditMode(false);
  };

  const columnDefs = useMemo<any[]>(
    () => [
      {
        field: "materialName",
        headerName: "Material Name",
        minWidth: 140,
        headerStyle: { backgroundColor: "#e9f5ff" },
        valueGetter: (params: any) =>
          materialDataMap.get(params.data.materialName) || "N/A",
      },
      {
        field: "fromLocation",
        headerName: "From Location",
        minWidth: 130,
        headerStyle: { backgroundColor: "#e9f5ff" },
        valueGetter: (params: any) =>
          allLocationsMap.get(params.data.fromLocation) || "N/A",
      },
      {
        field: "toLocation",
        headerName: "To Location",
        minWidth: 130,
        headerStyle: { backgroundColor: "#e9f5ff" },
        valueGetter: (params: any) =>
          allLocationsMap.get(params.data.toLocation) || "N/A",
      },
      {
        field: "quantityMoved",
        headerName: "Quantity",
        minWidth: 50,
        headerStyle: { backgroundColor: "#e9f5ff" },
        cellStyle: { textAlign: "left", paddingLeft: 10 },
      },
      {
        field: "unitOfMeasure",
        headerName: "Unit",
        minWidth: 50,
        headerStyle: { backgroundColor: "#e9f5ff" },
        valueGetter: (params: any) =>
          unitsofMeasureMap.get(params.data.unitOfMeasure) || "N/A",
      },
      {
        field: "movementDateTime",
        headerName: "Date & Time",
        minWidth: 170,
        headerStyle: { backgroundColor: "#e9f5ff" },
        valueGetter: (params: any) =>
          new Date(params.data.movementDateTime).toLocaleString("en-US", {
            year: "numeric",
            month: "numeric",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
          }),
      },
      {
        field: "action",
        headerName: "Action",
        minWidth: 90,
        maxWidth: 110,
        cellStyle: { padding: "10px 15px 0 0", textAlign: "left" },
        headerStyle: { backgroundColor: "#e9f5ff" },
        cellRenderer: (params: any) => (
          <div className="flex gap-x-3 justify-end">
            <button
              onClick={() => handleEdit(params.data)}
              className="p-1 hover:bg-gray-100 rounded cursor-pointer"
              title="Edit"
            >
              <img
                className="iconTd w-5 h-5"
                src={images.editIcon}
                alt="edit"
              />
            </button>
            <button
              onClick={() => handleDelete(params.data._id)}
              className="p-1 hover:bg-red-100 rounded cursor-pointer"
              title="Delete"
            >
              <Trash2 className="text-red-500" size={23} />
            </button>
          </div>
        ),
      },
    ],
    [allLocationsMap, orgUsersMap, materialDataMap, unitsofMeasureMap]
  );

  const defaultColDef = useMemo<any>(() => ({ editable: false }), []);
  const theme = useMemo<any>(() => gridTheme, []);

  return (
    <>
      <div className="px-2 py-4 mt-14 min-h-screen">
        <div className="flex justify-between items-center mb-4">
          <h2 className="ml-2">Tracking & Movement Logs</h2>
          <button
            className="flex items-center justify-center font-semibold gap-2 bg-[#0fa9a6] text-white border border-[#0fa9a6] px-4 py-2 rounded-full hover:bg-teal-600"
            onClick={() => {
              setModalOpen(true);
              setIsEditMode(false);
              setFormData(emptyForm);
            }}
          >
            <Plus size={20} /> Add Record
          </button>
        </div>

        <div className="tableBlock w-full">
          <div className="card">
            {rowData?.length > 0 ? (
              <div className="ag-theme-alpine w-full min-h-[400px] rounded-lg shadow-lg">
                <AgGridReact
                  rowData={rowData}
                  columnDefs={columnDefs}
                  theme={theme}
                  headerHeight={65}
                  rowHeight={50}
                  loading={loading}
                  defaultColDef={defaultColDef}
                  domLayout="autoHeight"
                  pagination
                  paginationPageSize={5}
                />
              </div>
            ) : (
              <div className="flex flex-col justify-center items-center h-64">
                <p className="text-gray-500 text-lg mb-4">
                  No movement records found
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      <Modal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        closeButton={false}
        modalClass="bg-white w-full max-w-4xl rounded-xl shadow-lg relative max-h-[80vh] flex flex-col"
      >
        <div className="pt-6 px-6 flex-1 overflow-y-auto">
          <h2 className="text-xl font-semibold text-gray-800 border-b pb-2 mb-6">
            {isEditMode
              ? "Update Tracking & Movement"
              : "Add Tracking & Movement"}
          </h2>
          <form
            onSubmit={handleSubmit}
            id="trackingMovementForm"
            className="flex flex-col gap-6 pb-20"
          >
            {/* Movement ID */}
            <div className="grid grid-cols-2 gap-6">
              {/* Material Name */}
              <div className="flex flex-col gap-2">
                <label
                  htmlFor="materialName"
                  className="text-sm font-medium text-gray-700"
                >
                  Material Name <span className="text-red-500">*</span>
                </label>
                <select
                  name="materialName"
                  value={formData.materialName}
                  onChange={handleChange}
                  required
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none"
                >
                  <option value="">Select Material</option>
                  {hazardousMaterials.map((m) => (
                    <option key={m._id} value={m._id}>
                      {m.materialName}
                    </option>
                  ))}
                </select>
              </div>
              {/* Movement Date & Time */}
              <div className="flex flex-col gap-2">
                <label
                  htmlFor="movementDateTime"
                  className="text-sm font-medium text-gray-700"
                >
                  Movement Date & Time <span className="text-red-500">*</span>
                </label>
                <input
                  name="movementDateTime"
                  id="movementDateTime"
                  type="datetime-local"
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none"
                  value={formData.movementDateTime}
                  onChange={handleChange}
                  aria-label="Movement Date and Time"
                  required
                />
              </div>
              {/* From Location */}
              <div className="flex flex-col gap-2">
                <label
                  htmlFor="fromLocation"
                  className="text-sm font-medium text-gray-700"
                >
                  From Location <span className="text-red-500">*</span>
                </label>
                <select
                  name="fromLocation"
                  value={formData.fromLocation}
                  onChange={handleChange}
                  required
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none"
                >
                  <option value="">Select From Location</option>
                  {allLocations.map((l) => (
                    <option key={l._id} value={l._id}>
                      {l.room} {l.floorNumber ? " - " + l.floorNumber : ""}
                    </option>
                  ))}
                </select>
              </div>
              {/* To Location */}
              <div className="flex flex-col gap-2">
                <label
                  htmlFor="toLocation"
                  className="text-sm font-medium text-gray-700"
                >
                  To Location <span className="text-red-500">*</span>
                </label>
                <select
                  name="toLocation"
                  value={formData.toLocation}
                  onChange={handleChange}
                  required
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none"
                >
                  <option value="">Select To Location</option>
                  {allLocations.map((l) => (
                    <option key={l._id} value={l._id}>
                      {l.room} {l.floorNumber ? " - " + l.floorNumber : ""}
                    </option>
                  ))}
                </select>
              </div>
              {/* Quantity Moved */}
              <div className="flex flex-col gap-2">
                <label
                  htmlFor="quantityMoved"
                  className="text-sm font-medium text-gray-700"
                >
                  Quantity Moved <span className="text-red-500">*</span>
                </label>
                <input
                  name="quantityMoved"
                  id="quantityMoved"
                  type="text"
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none"
                  value={formData.quantityMoved}
                  onChange={handleChange}
                  placeholder="Quantity"
                  aria-label="Quantity Moved"
                  required
                />
              </div>
              {/* Unit of Measure */}
              <div className="flex flex-col gap-2">
                <label
                  htmlFor="unitOfMeasure"
                  className="text-sm font-medium text-gray-700"
                >
                  Unit of Measure
                </label>
                <select
                  id="unitOfMeasure"
                  name="unitOfMeasure"
                  value={formData.unitOfMeasure}
                  onChange={handleChange}
                  disabled
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none"
                >
                  <option value="">Select Unit of Measure</option>
                  {unitsofMeasure?.map((u) => (
                    <option key={u._id} value={u._id}>
                      {u.unitSymbol}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex flex-col gap-2">
                <label className="text-sm font-medium text-gray-700">
                  Approved By<span className="text-red-500">*</span>
                </label>
                <select
                  name="approvedBy"
                  value={formData.approvedBy}
                  onChange={handleChange}
                  required
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none"
                >
                  <option value="">Select Approved By</option>
                  {orgUsersData.map((user: OrgUserType) => (
                    <option key={user._id as string} value={user._id as string}>
                      {user.firstName} {user.lastName} - {user.email}
                    </option>
                  ))}
                </select>
              </div>

              {/* Remarks */}
              <div className="flex flex-col gap-2">
                <label
                  htmlFor="remarks"
                  className="text-sm font-medium text-gray-700"
                >
                  Remarks
                </label>
                <textarea
                  name="remarks"
                  id="remarks"
                  rows={3}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none resize-y"
                  value={formData.remarks}
                  onChange={handleChange}
                  placeholder="Additional notes"
                  aria-label="Remarks"
                />
              </div>
            </div>
            <div className="h-20" />
          </form>
        </div>
        <div className="flex justify-end gap-3 p-3 bg-[#5A7192] w-full rounded-b-lg sticky bottom-0">
          <button
            type="button"
            className="px-4 py-2 bg-[#8497B0] text-white rounded-full hover:bg-teal-600 transition"
            onClick={handleModalClose}
          >
            Cancel
          </button>
          <button
            type="submit"
            form="trackingMovementForm"
            className="px-4 py-2 bg-[#00A8A8] text-white rounded-full hover:bg-teal-600 transition"
          >
            {isEditMode ? "Update" : "Add"}
          </button>
        </div>
      </Modal>
    </>
  );
};

export default TrackingMovementLogs;
